import { useCallback, useEffect, useState } from 'react';

const RUN_BROWSER_NOTIFICATIONS_ENABLED_KEY = 'velos.runBrowserNotifications.enabled';

export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

export function browserNotificationsAreAvailable() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getBrowserNotificationPermission(): BrowserNotificationPermission {
  if (!browserNotificationsAreAvailable()) return 'unsupported';
  return Notification.permission;
}

export function getRunBrowserNotificationsEnabled() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(RUN_BROWSER_NOTIFICATIONS_ENABLED_KEY) === 'true';
}

function setRunBrowserNotificationsEnabled(enabled: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(RUN_BROWSER_NOTIFICATIONS_ENABLED_KEY, String(enabled));
}

export function useBrowserNotificationPreference() {
  const [enabled, setEnabledState] = useState(getRunBrowserNotificationsEnabled);
  const [permission, setPermission] = useState<BrowserNotificationPermission>(
    getBrowserNotificationPermission,
  );

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== RUN_BROWSER_NOTIFICATIONS_ENABLED_KEY) return;
      setEnabledState(getRunBrowserNotificationsEnabled());
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setEnabled = useCallback(async (nextEnabled: boolean) => {
    if (!nextEnabled) {
      setRunBrowserNotificationsEnabled(false);
      setEnabledState(false);
      setPermission(getBrowserNotificationPermission());
      return;
    }

    if (!browserNotificationsAreAvailable()) {
      setRunBrowserNotificationsEnabled(false);
      setEnabledState(false);
      setPermission('unsupported');
      return;
    }

    const nextPermission =
      Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;

    setPermission(nextPermission);
    const nextPreference = nextPermission === 'granted';
    setRunBrowserNotificationsEnabled(nextPreference);
    setEnabledState(nextPreference);
  }, []);

  return { enabled, permission, setEnabled };
}
